import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, TrendingUp, Wallet, AlertTriangle, Trophy } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { api, money } from "@/lib/api";
import { SatisfactionTrend } from "@/components/SatisfactionTrend";

const Kpi = ({ icon: Icon, label, value, sub, tone = "text-slate-100", testId }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4 sm:p-6 min-w-0">
    <p className="flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2"><Icon className="h-4 w-4" />{label}</p>
    <p data-testid={testId} className={`num text-xl sm:text-3xl font-bold break-words ${tone}`}>{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </div>
);

const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-US", { month: "short", year: "2-digit" });

export default function Revenue() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/reports/revenue").then((r) => setD(r.data)).catch(() => setErr(true)); }, []);
  if (err) return <div className="min-h-[60vh] grid place-items-center text-slate-400" data-testid="revenue-error">Could not load revenue data. Please refresh.</div>;
  if (!d) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>;
  const cur = d.currency;
  const data = d.months.map((m) => ({ ...m, label: monthLabel(m.month) }));
  const maxTop = Math.max(1, ...d.top_clients.map((c) => c.paid));
  const axis = { stroke: "#64748b", fontSize: 11, fontFamily: "JetBrains Mono" };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 fade-up" data-testid="revenue-page">
      <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-2">Team</p>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mb-10">Revenue</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-8">
        <Kpi icon={TrendingUp} label="Collected this month" value={money(d.collected_this_month, cur)} tone="text-emerald-400" testId="rev-this-month" />
        <Kpi icon={Wallet} label="Collected (all time)" value={money(d.collected_total, cur)} testId="rev-total" />
        <Kpi icon={Wallet} label="Outstanding" value={money(d.outstanding, cur)} sub={`${d.outstanding_count} unpaid invoice${d.outstanding_count === 1 ? "" : "s"}`} tone="text-amber-400" testId="rev-outstanding" />
        <Kpi icon={AlertTriangle} label="Overdue" value={money(d.overdue, cur)} sub={`${d.overdue_count} past due`} tone="text-rose-400" testId="rev-overdue" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <section className="lg:col-span-8 rounded-xl border border-slate-800 bg-slate-900/70 p-6" data-testid="revenue-chart">
          <h2 className="font-display text-lg font-semibold mb-1">Last 12 months</h2>
          <p className="text-xs text-slate-500 mb-6">Collected (by payment date) vs invoiced (by issue date)</p>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data}>
                <CartesianGrid stroke="#1e293b" vertical={false} />
                <XAxis dataKey="label" {...axis} />
                <YAxis {...axis} width={80} tickFormatter={(v) => money(v, cur)} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8 }} formatter={(v) => money(v, cur)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="collected" name="Collected" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Line dataKey="invoiced" name="Invoiced" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </section>
        <section className="lg:col-span-4 rounded-xl border border-slate-800 bg-slate-900/70 p-6" data-testid="top-clients">
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold mb-6"><Trophy className="h-5 w-5 text-amber-400" />Top clients</h2>
          {d.top_clients.length === 0 && <p className="text-sm text-slate-500">No paid invoices yet.</p>}
          <div className="space-y-4">
            {d.top_clients.map((c, i) => (
              <Link key={c.id} to={`/invoices?client=${c.id}`} className="block group" data-testid={`top-client-${i}`}>
                <div className="flex justify-between text-sm mb-1.5"><span className="text-slate-200 group-hover:text-emerald-400 truncate">{c.name}{c.company ? <span className="text-slate-500"> · {c.company}</span> : null}</span><span className="num font-bold">{money(c.paid, cur)}</span></div>
                <div className="h-2 rounded-full bg-slate-800 overflow-hidden"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${(c.paid / maxTop) * 100}%` }} /></div>
              </Link>
            ))}
          </div>
        </section>
      </div>
      <SatisfactionTrend />
    </div>
  );
}
